<script setup>
import {computed, onMounted, onUnmounted, ref, watch} from 'vue'
import {useStore} from 'vuex'
import { bbox } from '@turf/turf'
import { MapSource, MapLayer } from '@/components/map'
import { useLayerManager } from '@/composables/useLayerManager'
import { createLayerId, createSourceId } from '@/components/layers/types'
import { buildSubscriptionTileUrl } from '@/lib/tileUrl'

const store = useStore()
const map = computed(() => props.map)
const layerManager = useLayerManager(map)
const settings = computed(() => store.getters['user/getUserSettings'])

const results = ref([])
const activeResult = ref(null)

// Get subscription results from the store (batch-fetched)
const subscriptionResults = computed(() =>
  store.getters['layers/getSubscriptionResults'](props.orgId, props.code)
)

const emit = defineEmits(['isLoaded'])
const props = defineProps({
  map: {
    type: Object,
    required: true
  },
  layerId: {
    type: String,
    required: true
  },
  beforeId: {
    type: String,
    required: true
  },
  groupId: {
    type: String,
    required: true
  },
  options: {
    type: Object,
    required: true
  },
  layer: {
    type: Object,
    required: true
  },
  isVisible: {
    type: Boolean,
    required: true
  },
  code: {
    type: String,
    required: true
  },
  orgId: {
    type: Number,
    required: true
  }
})

// we don't need to re-render the map source and layer when the layer is
// not visible, it will be handled by the parent component and mapbox api
const isVisibleChecked = ref(props.isVisible)

const statusToLabel = {
  '100': 'Pending Approval',
  '-100': 'Rejected',
  '-9': 'Preparing Data (Failed)',
  '-8': 'Cleaning Up (Failed)',
  '-7': 'Uploading Tiles (Failed)',
  '-6': 'Generating Tiles (Failed)',
  '-5': 'Merging Quads (Failed)',
  '-4': 'Uploading Data (Failed)',
  '-3': 'Delivering Data (Failed)',
  '-2': 'Ordering Data (Failed)',
  '-1': 'Starting (Failed)',
  '0': 'Done',
  '1': 'Starting',
  '2': 'Ordering Data',
  '3': 'Delivering Data',
  '4': 'Uploading Data',
  '5': 'Merging Quads',
  '6': 'Generating Tiles',
  '7': 'Uploading Tiles',
  '8': 'Cleaning Up',
  '9': 'Preparing Data',
  '10': 'Generating Tiles'
}


/**
 * Generate a human-readable label from a Planet resource_id.
 * Parses patterns like "ABOVEGROUND_CARBON_DENSITY_v1.0.0_30" or "LST-AMSR2_V1.0_100"
 *
 * @param {string} resourceId - The Planet resource identifier
 * @returns {string} Human-readable label
 */
const generateResourceLabel = (resourceId) => {
  if (!resourceId) return 'Unknown'

  // Try to match pattern: NAME_vX.X.X_RES or NAME_VX.X_RES
  const match = resourceId.match(/^(.+?)_([vV][\d.]+)_(\d+)$/)

  if (match) {
    const [, name, version, resolution] = match
    // Convert underscores/hyphens to spaces and title case
    const labelName = name
      .replace(/_/g, ' ')
      .replace(/-/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')

    return `${labelName} ${resolution}m (${version})`
  }

  // Fallback: just format the resource_id
  return resourceId
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

// Computed label for the current layer's resource_id
const resourceLabel = computed(() => {
  const resourceId = props.layer.data?.parameters?.id
  return generateResourceLabel(resourceId)
})

// Unified layer ID helpers for multiple results
const getRasterSourceId = (resultId) => createSourceId(props.groupId, `raster-source-${resultId}`)
const getRasterLayerId = (resultId) => createLayerId(props.groupId, `raster-${resultId}`)

// Mapbox skips requests for tiles fully outside this bbox — saves the
// viewport-wide 404 storm for layers whose AOI is a small patch.
const layerBounds = computed(() => {
  if (!props.layer?.geometry) return null
  try {
    return bbox({ type: 'Feature', geometry: props.layer.geometry })
  } catch (e) {
    return null
  }
})

// Track which layer is currently registered
const registeredLayerId = ref(null)

// props.code is layers.code (parent); the bucket key uses layer_subscription.code,
// which lives on the nested type-specific row at props.layer.code.
// Computed so it tracks the STS session — initial render before the session
// arrives yields null, and the source reactively materializes once the
// credentials land instead of trying to mount with tiles: [null].
// props.layer is forwarded to the builder so legacy rows (no storage_bucket /
// storage_prefix) fall through to the Tulum proxy instead of direct-S3.
const activeTileUrl = computed(() => {
  if (!activeResult.value || !props.orgId || !props.layer?.code) return null
  return buildSubscriptionTileUrl(props.orgId, props.layer.code, activeResult.value.id, props.layer)
})

const initializeResults = async (rawResults) => {
  if (!rawResults?.length) return

  const sortedResults = [...rawResults].sort((a, b) => new Date(a.item_datetime) - new Date(b.item_datetime))

  results.value = sortedResults.map((i) => ({
    ...i,
    isRendered: false
  }))

  activeResult.value = results.value[results.value.length - 1]

  // Register the active layer
  if (activeResult.value) {
    const layerId = getRasterLayerId(activeResult.value.id)
    layerManager.registerLayer(props.groupId, layerId)
    registeredLayerId.value = layerId
  }

  emit('isLoaded', true)
}

onMounted(() => {
  // Allow loading results for done (0) and generate_tiles (10) states.
  // At status 10 the results JSON exists and tiles are being generated
  // progressively — earlier results may already have viewable tiles.
  if (props.layer.status !== 0 && props.layer.status !== 10) return

  // Use results from store if available (batch-fetched)
  if (subscriptionResults.value) {
    initializeResults(subscriptionResults.value)
  }
})

// Watch for subscription results to be loaded from store (async batch fetch)
watch(subscriptionResults, (newResults) => {
  if (newResults && (props.layer.status === 0 || props.layer.status === 10) && !results.value.length) {
    initializeResults(newResults)
  }
})

onUnmounted(() => {
  // Unregister the current layer
  if (registeredLayerId.value) {
    layerManager.unregisterLayer(props.groupId, registeredLayerId.value)
  }
})

watch(() => props.isVisible, (value) => {
  if (value) {
    isVisibleChecked.value = true
  }
})

// When active result changes, update registered layer
watch(activeResult, (newResult, oldResult) => {
  if (oldResult && registeredLayerId.value) {
    layerManager.unregisterLayer(props.groupId, registeredLayerId.value)
  }
  if (newResult) {
    const layerId = getRasterLayerId(newResult.id)
    layerManager.registerLayer(props.groupId, layerId)
    registeredLayerId.value = layerId
  }
})

// Watch for year selection changes from the store (triggered by detail page dropdown)
const storeSelection = computed(() => store.getters['layers/getPlanetVarsSelection'](props.code))
watch(storeSelection, (selection) => {
  if (!selection || !results.value.length) return
  if (selection.mode === 'all') {
    // Show most recent result
    activeResult.value = results.value[results.value.length - 1]
  } else {
    const { year } = selection
    const matchingResults = results.value.filter(r => {
      const d = new Date(r.item_datetime)
      return d.getUTCFullYear() === year
    })
    if (matchingResults.length > 0) {
      activeResult.value = matchingResults[matchingResults.length - 1]
    }
  }
})

// Watch for active result changes from the store (triggered by chart hover in Layer Detail)
const storeActiveResult = computed(() => store.getters['layers/getActiveResult'](props.code))
watch(storeActiveResult, (newResult) => {
  if (newResult && results.value.length > 0) {
    // Find the matching result in our local results array
    const matchingResult = results.value.find(r => r.id === newResult.id)
    if (matchingResult && activeResult.value?.id !== matchingResult.id) {
      activeResult.value = matchingResult
    }
  }
})
</script>

<template>
  <div v-if="isVisibleChecked">
    <MapSource
      v-if="map && activeResult && activeTileUrl"
      :key="activeResult.id"
      :map="map"
      type="raster"
      :id="getRasterSourceId(activeResult.id)"
      :options="{
        tiles: [activeTileUrl],
        tileSize: 256,
        maxzoom: 16,
        ...(layerBounds ? { bounds: layerBounds } : {})
      }"
    >
      <MapLayer
        :map="map"
        :id="getRasterLayerId(activeResult.id)"
        :source="getRasterSourceId(activeResult.id)"
        type="raster"
        :options="{
          ...props.options,
          layout: {
            visibility: isVisible ? 'visible' : 'none'
          }
        }"
        :before-id="beforeId"
        :group-id="groupId"
        @loaded="() => { activeResult.isRendered = true }"
      ></MapLayer>
    </MapSource>
  </div>
</template>
